import { useAxios, useSubjectMember } from '@healthblocks-io/core'
import { isAfter } from 'date-fns'
import { addMonths, endOfDay, isFuture, isToday } from 'date-fns/esm'
import { useCallback } from 'react'

import { useAxiosAsync } from '@/@bothrs-react/hooks/useAxiosAsync'
import { useIntegrationAxios } from '@/modules/healthblocks/hooks/useIntegrationAxios'

import type { AxiosResponse } from 'axios'
import type { Encounter } from '../types/encounter.types'

function useEncounters() {
  const hbAxios = useAxios()
  const axios = useIntegrationAxios()
  const member = useSubjectMember()

  const getEncounters = useCallback(() => {
    return axios
      .get<(Encounter & { trigger: string })[]>('/encounters', {
        params: { memberId: member?.id },
        cache: {
          override: true,
        },
      })
      .then((value: AxiosResponse<(Encounter & { trigger: string })[]>) => {
        return value?.data
          ? {
              ...value,
              data: value?.data?.filter((encounter) => encounter.status !== 'cancelled'),
            }
          : value
      })
  }, [axios, member?.id])

  const getEncountersForToday = useCallback(() => {
    return getEncounters().then((value: AxiosResponse<(Encounter & { trigger: string })[]>) => {
      return value?.data
        ? {
            ...value,
            data: value?.data?.filter((encounter) => isToday(new Date(encounter.start))),
          }
        : value
    })
  }, [getEncounters])

  const getUpcomingEncounter = useCallback(() => {
    return getEncounters().then((value: AxiosResponse<(Encounter & { trigger: string })[]>) => {
      return {
        ...value,
        data: value?.data?.find((encounter) => {
          const startDate = new Date(encounter.start)
          const nextMonth = addMonths(endOfDay(new Date()), 1)
          return isFuture(startDate) && !isAfter(startDate, nextMonth)
        }),
      }
    })
  }, [getEncounters])

  const getEncounterById = useCallback(
    (id: number) => {
      return hbAxios.get<Encounter & { trigger: string }>(`/encounter/${id}`, {
        params: { id: member?.id },
      }) as Promise<AxiosResponse<Encounter & { trigger: string }>>
    },
    [hbAxios, member?.id],
  )

  const getEncountersByTrigger = useCallback(
    (trigger: string) => {
      return hbAxios.get<(Encounter & { trigger: string })[]>(`/encounter`, {
        params: { trigger, subjectId: member?.id },
      }) as Promise<AxiosResponse<(Encounter & { trigger: string })[]>>
    },
    [hbAxios, member?.id],
  )

  const confirmEncounterTime = useCallback(
    (parameters: { synopsPatientId: number; synopsEncounterId: string }) => {
      return axios.post('/synops/confirm-encounter', parameters)
    },
    [axios],
  )

  return {
    getEncounters: useAxiosAsync(getEncounters, true),
    getEncountersByTrigger: useAxiosAsync(getEncountersByTrigger, true),
    getEncountersForToday: useAxiosAsync(getEncountersForToday, true),
    getUpcomingEncounter: useAxiosAsync(getUpcomingEncounter, true),
    getEncounterById: useAxiosAsync(getEncounterById, true),
    confirmEncounterTime: useAxiosAsync(confirmEncounterTime),

    encountersFetcher: getEncounters,
    encountersByTriggerFetcher: getEncountersByTrigger,
    encountersForTodayFetcher: getEncountersForToday,
    upcomingEncounterFetcher: getUpcomingEncounter,
    encounterByIdFetcher: getEncounterById,
    confirmEncounterTimeFetcher: confirmEncounterTime,
  }
}

export { useEncounters }
